import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { QueryKeys } from 'src/constants/queryKeys';

import axios from 'axios';
import qs from 'qs';
import { useConfig } from '@app/config';
import { useAddress } from '@thirdweb-dev/react';

export type MerklUserRewards = Record<
  string,
  {
    chainId: number;
    accumulated: string;
    decimals: number;
    symbol: string;
    unclaimed: string;
    reasons: Record<
      string,
      {
        accumulated: string;
        unclaimed: string;
      }
    >;
    proof: string[];
  }
>;

const fetcher = async (
  address: string | undefined,
  apiUrl: string | undefined,
  chainIds: number[]
) => {
  if (!address || !apiUrl || !chainIds.length) {
    return null;
  }

  const res = await Promise.all(
    chainIds.map(async chainId => {
      const queryParams = {
        user: address,
        chainId
      };

      const query = qs.stringify(queryParams, {
        encodeValuesOnly: true
      });

      const { data } = await axios.get<MerklUserRewards>(
        `${apiUrl}/userRewards?${query}`
      );

      return Object.keys(data).reduce<MerklUserRewards>((r, key) => {
        const value = data[key];

        r[key] = { ...value, chainId };

        return r;
      }, {});
    })
  );

  return res.reduce((r, item) => {
    r = {
      ...r,
      ...item
    };

    return r;
  }, {});
};

export const useMerklUserRewards = (
  chainIds: number[],
  options: UseQueryOptions<MerklUserRewards | null> = {}
) => {
  const address = useAddress();
  const config = useConfig();
  const apiUrl = config?.URLS.merklApiUrl;

  return useQuery<MerklUserRewards | null>(
    [QueryKeys.MERKL_USER_REWARDS, address, apiUrl, chainIds.join(',')],
    async () => {
      return await fetcher(address, apiUrl, chainIds);
    },
    {
      staleTime: 60000,
      refetchOnWindowFocus: false,
      enabled: !!(config && config.MERKL.DISTRIBUTOR),
      ...options
    }
  );
};
